import { Button } from "@hortis/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@hortis/ui/dialog";
import { toast } from "@hortis/ui/sonner";
import { namedOperations } from "generated/graphql";
import { type ReactNode, type RefObject } from "react";
import { queryClient } from "src/app";
import { archiveObservationDocument } from "src/features/material-page/create-observation-dialog/graphql";
import { useGqlMutation } from "src/utils/hooks/graphql";

export const TrashObservationDialog = ({
  observationId,
  open,
  onOpenChange,
  onSuccess: onSuccessProp,
  triggerRef,
  children,
}: {
  observationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  triggerRef?: RefObject<HTMLButtonElement | null>;
  children?: ReactNode;
}) => {
  const archiveObservation = useGqlMutation({
    mutation: archiveObservationDocument,
    onError: () => {
      toast.error("Failed to delete observation");
    },
    onSuccess: (res) => {
      if (!res.archiveObservation.success) {
        toast.error("Failed to delete observation");
      }

      toast.success("Observation deleted");
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetMaterial],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.PlantMaterials],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetDashboard],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.MapListMaterials],
      });
      if (onSuccessProp) {
        onSuccessProp();
      }
      onOpenChange(false);
    },
  });

  const handleDeleteObservation = async () => {
    await archiveObservation.mutateAsync({
      id: observationId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children}
      <DialogContent
        data-cy="delete-observation-dialog"
        triggerRef={triggerRef}
      >
        <DialogHeader>
          <DialogTitle>Delete this observation?</DialogTitle>
          <DialogDescription>
            This will delete the observation, captured photos will not be
            deleted.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button size="sm" testId="close-delete-observation-dialog">
              Cancel
            </Button>
          </DialogClose>
          <Button
            size="sm"
            onClick={handleDeleteObservation}
            loading={archiveObservation.isPending}
            variant="destructive"
            testId="submit-delete-observation"
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
